import { derived, get, writable } from 'svelte/store';

import { GroupsService } from '@client/services/groups';
import { VirtualBackgroundService } from '@client/services/virtual-background';
import { VirtualBackgroundRoutingService } from '@client/services/virtual-background-routing';

import { bizStudioSettingsStore } from '@client/stores/page-settings';

import { STUDIO_FILTERS } from '@client/enums';

import { sortCollection } from '@common/utils/arrays';
import { debounce } from '@common/utils/debounce';
import { merge } from '@common/utils/objects';

/**
 * @typedef {Object} Background
 * @property {string} _id
 * @property {string} rulename
 * @property {Object} template
 * @property {Object} params
 * @property {boolean} status
 * @property {boolean} [active]
 * @property {Array} [senders]
 * @property {boolean} [loading]
 * @property {string} [html]
 */

// State stores
export const filters = writable(merge({}, { ...STUDIO_FILTERS }, get(bizStudioSettingsStore).filters));
export const backgrounds = writable([]);
export const filteredRecords = writable([]);
export const totalRecords = writable(0);
export const updating = writable(false);
export const initialized = writable(false);
export const originalRecordCount = writable(0);

// Data dependency stores
export const groups = writable([]);
export const routing = writable([]);

// Derived stores
export const hasBackgrounds = derived(
  [initialized, originalRecordCount],
  ([$initialized, $originalRecordCount]) => $initialized && $originalRecordCount > 0
);

/**
 * Initialize filters from query params and settings
 * @param {number} pageQueryParam
 * @param {number} perPageQueryParam
 * @param {string} searchQueryParam
 */
export function initializeFilters(pageQueryParam, perPageQueryParam, searchQueryParam) {
  filters.update((current) => {
    const updated = { ...current };

    // Update labels from STUDIO_FILTERS
    Object.keys(updated.sorting.options).forEach((key) => {
      updated.sorting.options[key].label = STUDIO_FILTERS.sorting.options[key].label;
    });

    // Initialize search from query param if not already set
    if (!updated.search) {
      updated.search = searchQueryParam || '';
    }

    // Update page from query param
    if (pageQueryParam && pageQueryParam !== updated.paging.page) {
      updated.paging.page = pageQueryParam;
    }

    // Update perPage from query param
    if (perPageQueryParam && perPageQueryParam !== updated.paging.perPage) {
      updated.paging.perPage = perPageQueryParam;
    }

    return updated;
  });

  // Sync to settings store
  syncFiltersToSettings();
}

/**
 * Sync current filters to settings store
 */
function syncFiltersToSettings() {
  bizStudioSettingsStore.update((store) => ({
    ...store,
    filters: { ...get(filters) }
  }));
}

/**
 * Generate HTML preview for a background
 * @param {Background} background
 * @returns {Promise<string>}
 */
export async function getBackgroundPreview(background) {
  let email = 'adminuser';

  if (background.html) {
    return background.html;
  }

  let getSender = background?.senders?.[0];

  if (getSender) {
    const username = getSender.username || email;

    if (username !== '*') {
      email = username;
    }
  }

  const data = {
    username: email,
    scratchblockname: background.rulename,
    scratchtemplate: background.template,
    scratchparams: background.params,
    params: background.params,
    prependLines: 0,
    testData: {
      testsender: email
    }
  };

  background.loading = true;

  try {
    const response = await VirtualBackgroundService.preview(data);
    const { rendered } = response;

    background.loading = false;
    background.html = rendered;

    return rendered;
  } catch (error) {
    background.loading = false;
    throw error;
  }
}

/**
 * Update backgrounds list with filtering, sorting, and pagination
 * @param {boolean} skipFetch - Skip fetching from service (use cached data)
 */
async function refreshBackgrounds(skipFetch = false) {
  updating.set(true);

  const currentFilters = get(filters);
  const currentRouting = get(routing);
  const currentGroups = get(groups);
  const isInitialized = get(initialized);
  let allBackgrounds = get(backgrounds);

  try {
    if (!skipFetch) {
      // Fetch fresh data from service
      const fetchedBackgrounds = await VirtualBackgroundService.getAll();

      originalRecordCount.set(fetchedBackgrounds.length);

      // Enhance backgrounds with routing and sender information
      if (currentRouting.length) {
        const activeIds = currentRouting.map(({ blockname, status }) => status && blockname).filter(Boolean);

        fetchedBackgrounds.forEach((item) => {
          const match = currentRouting.find(({ blockname }) => blockname === item.rulename);
          const sendergroups = currentGroups.filter(
            ({ groupname }) => groupname === match?.sendergroups?.[0]?.groupname
          );
          const senders = (sendergroups?.length && sendergroups[0]?.senders) || [];

          item.active = activeIds.includes(item.rulename);
          item.senders = senders;
        });
      }

      backgrounds.set(fetchedBackgrounds);
      allBackgrounds = fetchedBackgrounds;
    }

    // Apply search filter
    let filtered = [...allBackgrounds];
    if (currentFilters.search.length) {
      filtered = allBackgrounds.filter((item) =>
        JSON.stringify(Object.values(item).join(',')).toLowerCase().includes(currentFilters.search.toLowerCase())
      );
    }

    const recordCount = filtered.length;
    const sortDirection = currentFilters.sorting.dir === 'asc' ? -1 : 1;

    // Apply sorting and pagination
    const paginated = sortCollection(filtered, currentFilters.sorting.key, sortDirection).slice(
      (currentFilters.paging.page - 1) * currentFilters.paging.perPage,
      currentFilters.paging.page * currentFilters.paging.perPage
    );

    filteredRecords.set(paginated);
    totalRecords.set(currentFilters.search.length ? recordCount : allBackgrounds.length);

    initialized.set(true);
  } catch (error) {
    console.error('Error refreshing backgrounds:', error);
    throw error;
  } finally {
    updating.set(false);
  }
}

/**
 * Load initial data (groups, routing, backgrounds)
 * @returns {Promise<void>}
 */
export async function loadBackgrounds() {
  updating.set(true);

  try {
    const [fetchedRouting, fetchedGroups] = await Promise.all([
      VirtualBackgroundRoutingService.getAll(),
      GroupsService.getAll()
    ]);

    routing.set(fetchedRouting);
    groups.set(fetchedGroups);

    syncFiltersToSettings();
    await refreshBackgrounds();
  } catch (error) {
    console.error('Error loading backgrounds:', error);
    throw error;
  } finally {
    updating.set(false);
  }
}

/**
 * Handle search filter change with debouncing
 */
export const onSearchFilterChange = debounce(() => {
  filters.update((current) => ({
    ...current,
    paging: { ...current.paging, page: 1 }
  }));
  syncFiltersToSettings();
  refreshBackgrounds(true);
}, 250);

/**
 * Handle sort change
 * @param {any} selected
 * @param {string} key
 * @param {string} dir
 */
export function onSortChange(selected, key, dir) {
  filters.update((current) => ({
    ...current,
    sorting: { ...current.sorting, key, dir }
  }));
  syncFiltersToSettings();
  refreshBackgrounds(true);
}

/**
 * Handle page change
 * @param {number} page
 */
export function onChangePage(page) {
  filters.update((current) => ({
    ...current,
    paging: { ...current.paging, page }
  }));
  syncFiltersToSettings();
  refreshBackgrounds(true);
}

/**
 * Handle records per page change
 * @param {number} perPage
 */
export function onChangeRecords(perPage) {
  filters.update((current) => ({
    ...current,
    paging: { ...current.paging, perPage }
  }));
  syncFiltersToSettings();
  refreshBackgrounds(true);
}

/**
 * Clone a background
 * @param {Background} background
 * @param {function(string): string} generateUniqueRulename
 * @returns {Promise<{success: boolean, rulename?: string, error?: string}>}
 */
export async function cloneBackground(background, generateUniqueRulename) {
  updating.set(true);

  try {
    const rulename = generateUniqueRulename(background.rulename);

    const response = await VirtualBackgroundService.update({
      rulename,
      template: background.template,
      params: background.params,
      status: false,
      _id: null,
      _newObject: true
    });

    if (response.status === 500) {
      return {
        success: false,
        error: `Failed to clone background: ${background.rulename}`
      };
    }

    return {
      success: true,
      rulename: response.rulename
    };
  } catch (error) {
    console.error('Error cloning background:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  } finally {
    updating.set(false);
  }
}

/**
 * Delete a background
 * @param {Background} background
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteBackground(background) {
  updating.set(true);

  try {
    const response = await VirtualBackgroundService.delete(background);

    if (response.status >= 400) {
      return {
        success: false,
        error: `Failed to delete background: ${background.rulename}`
      };
    }

    // Remove from local state
    backgrounds.update((current) => {
      const filtered = current.filter((item) => item.rulename !== background.rulename);
      return filtered;
    });

    // Update filtered records
    const currentFilters = get(filters);
    const currentFilteredRecords = get(filteredRecords);
    const updatedFilteredRecords = currentFilteredRecords.filter((item) => item.rulename !== background.rulename);

    filteredRecords.set(updatedFilteredRecords);

    // Adjust page if needed
    if (!updatedFilteredRecords.length && currentFilters.paging.page > 1) {
      filters.update((current) => ({
        ...current,
        paging: { ...current.paging, page: current.paging.page - 1 }
      }));
    }

    // Check if we should reset original count
    const allBackgrounds = get(backgrounds);
    if (!allBackgrounds.length) {
      originalRecordCount.set(0);
    }

    // Refresh to recalculate totals
    syncFiltersToSettings();
    await refreshBackgrounds(true);

    return { success: true };
  } catch (error) {
    console.error('Error deleting background:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  } finally {
    updating.set(false);
  }
}

/**
 * Rename a background
 * @param {Background} background - The background to rename
 * @param {string} newName - The new rulename
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function renameBackground(background, newName) {
  updating.set(true);

  try {
    const response = await VirtualBackgroundService.update({
      ...background,
      rulename: newName
    });

    if (response?.status >= 400) {
      return {
        success: false,
        error: `Failed to rename background: ${background.rulename}`
      };
    }

    await refreshBackgrounds();

    return { success: true };
  } catch (error) {
    console.error('Error renaming background:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  } finally {
    updating.set(false);
  }
}

/**
 * Reset all stores to initial state
 */
export function resetStore() {
  filters.set(merge({}, { ...STUDIO_FILTERS }, get(bizStudioSettingsStore).filters));
  backgrounds.set([]);
  filteredRecords.set([]);
  totalRecords.set(0);
  updating.set(false);
  initialized.set(false);
  originalRecordCount.set(0);
  groups.set([]);
  routing.set([]);
}
